/**
 * Marketing attribution -> cart (task #1118).
 *
 * titan-web / Drupal / this theme write the _img_marketing_params cookie on
 * .imgacademy.com: every UTM and ad click-id, one "session_N" object per
 * param-bearing visit, encodeURIComponent(JSON.stringify(sessions)). This
 * file is the READER: it copies the cookie's JSON onto the cart as ONE
 * top-level attribute, marketing_params, so Shopify carries it to the order
 * (Additional details / note_attributes). The customer / participants
 * attributes - and their marketing_data - are not touched.
 *
 * SITEWIDE ON PURPOSE: every purchase path passes a page on this domain
 * (the camp selector, share carts, /pages/router, /pages/personal-coach,
 * /pages/add-to-cart, product pages, /cart), so one reader covers all carts.
 * Loaded by layout/theme.liquid AND by every {% layout none %} purchase page.
 *
 * Writes only when the cart's value differs from the cookie: one GET /cart.js
 * per page view, and a cart/update.js carrying marketing_params alone.
 * cart/update.js replaces only the attributes it names, so customer /
 * participants written by other scripts are left as they are.
 *
 * Two cookie rules from the writer's contract (MARKETING_PARAMS_HANDOFF):
 *  - split document.cookie BEFORE decoding: a value may hold an encoded ; or ,
 *  - decode the value exactly ONCE: click-ids stay single-encoded.
 *
 * window.imgMarketingParams.sync() returns a Promise, for pages that leave
 * for /checkout right after writing the cart; syncWithin(ms) is the same but
 * settles after ms at the latest, so a slow request never holds a shopper up
 * (add-to-cart.js, router.js - the routes that skip the /cart page).
 *
 * EVERY cart gets marketing_params, cookie or not - buildSessions() combines
 * whatever is actually available, UTM leading, rather than either/or:
 *   1. _img_marketing_params (UTM / click-ids) - when present, goes first.
 *   2. _img_source_data - CRM's own doc_referrer/crm_source/crm_tactic
 *      object, written on EVERY visit regardless of campaign tagging. ADDED
 *      after step 1's sessions (not skipped just because UTM data exists -
 *      both can and do land together). The browser can hold several
 *      cookies of this same name (different path/domain scopes); ALL valid
 *      ones are collected - not just the first - capped at
 *      MAX_SOURCE_DATA_COOKIES (5) so a pile-up of stale cookies can't
 *      bloat the order.
 *   3. DEFAULT_SESSIONS (utm_source: "direct") - only when BOTH of the
 *      above have nothing at all.
 * Every entry lands as its own session_N, renumbered across both sources
 * together so step 1 and step 2 can never collide on the same key.
 * Without this, an order with no campaign tagging looks identical to one
 * where the script never ran at all - there's no way to tell "verified no
 * attribution" from "broken/never fired" just by reading Additional
 * details. A value present on every order removes that ambiguity.
 */
(function () {
  var COOKIE = '_img_marketing_params';
  var SOURCE_COOKIE = '_img_source_data';
  var MAX_SOURCE_DATA_COOKIES = 5;
  var ATTRIBUTE = 'marketing_params';
  var DEFAULT_SESSIONS = { session_1: { utm_source: 'direct' } };
  var pending = null;

  /**
   * Reads a cookie by name and JSON.parses its value, decoding exactly once
   * (the value may already have its outer braces literal - as written by
   * some sources - or be fully percent-encoded; decodeURIComponent handles
   * both, since characters with no percent-encoding simply pass through).
   * Returns null when the cookie is absent, empty, unreadable, or not a
   * non-empty plain object.
   */
  function readJsonCookie(name, warnLabel) {
    var parts = (document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(name + '=') !== 0) {
        continue;
      }
      var raw = part.substring(name.length + 1);
      if (!raw) {
        return null;
      }
      try {
        var value = JSON.parse(decodeURIComponent(raw));
        if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length) {
          return value;
        }
      } catch (e) {
        console.warn('[' + warnLabel + '] unreadable cookie, skipped', e);
      }
      return null;
    }
    return null;
  }

  /**
   * The _img_marketing_params cookie's sessions object, or null when absent
   * or unreadable. Mirrors readSessions() in titan-web marketing-params.client.ts.
   */
  function readSessions() {
    return readJsonCookie(COOKIE, 'marketing_params');
  }

  /**
   * ALL valid _img_source_data cookies, not just the first - the browser can
   * hold several cookies of the same name scoped to different paths/domains,
   * and document.cookie returns every one of them. Scans the WHOLE cookie
   * string rather than stopping at the first match, parses each instance
   * independently (one unreadable instance does not block the rest), and
   * caps at MAX_SOURCE_DATA_COOKIES so a pile-up of stale cookies can't blow
   * up the size of what lands on the order.
   */
  function readAllSourceData() {
    var parts = (document.cookie || '').split(';');
    var found = [];
    for (var i = 0; i < parts.length && found.length < MAX_SOURCE_DATA_COOKIES; i++) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(SOURCE_COOKIE + '=') !== 0) {
        continue;
      }
      var raw = part.substring(SOURCE_COOKIE.length + 1);
      if (!raw) {
        continue;
      }
      try {
        var value = JSON.parse(decodeURIComponent(raw));
        if (value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length) {
          found.push(value);
        }
      } catch (e) {
        console.warn('[marketing_params:source_data] unreadable cookie instance, skipped', e);
      }
    }
    return found;
  }

  /**
   * The written value: UTM/click-id sessions FIRST (when present), then
   * every _img_source_data cookie instance appended after them - not
   * either/or, both when both exist - renumbered session_1..session_N in
   * that combined order so the two sources can never collide on the same
   * key. Falls back to DEFAULT_SESSIONS only when NEITHER source has
   * anything to offer.
   */
  function buildSessions() {
    var combined = {};
    var index = 1;

    var utmSessions = readSessions();
    if (utmSessions) {
      var utmKeys = Object.keys(utmSessions);
      for (var i = 0; i < utmKeys.length; i++) {
        combined['session_' + index] = utmSessions[utmKeys[i]];
        index++;
      }
    }

    var sourceData = readAllSourceData();
    for (var j = 0; j < sourceData.length; j++) {
      combined['session_' + index] = sourceData[j];
      index++;
    }

    return (index === 1) ? DEFAULT_SESSIONS : combined;
  }

  function sync() {
    if (pending) {
      return pending;
    }
    if (!window.fetch) {
      return Promise.resolve(false);
    }
    // UTM/click-ids lead, _img_source_data cookies follow, "direct" only
    // when neither exists (see file header) - so every order carries
    // marketing_params and nothing reads as a silent gap.
    var sessions = buildSessions();
    var value = JSON.stringify(sessions);

    pending = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        var current = cart && cart.attributes ? cart.attributes[ATTRIBUTE] : null;
        if (current === value) {
          return false;
        }
        var attributes = {};
        attributes[ATTRIBUTE] = value;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes })
        }).then(function (res) { return res.ok; });
      })
      .catch(function (e) {
        // Attribution must never get in the way of a purchase.
        console.warn('[marketing_params] cart write failed', e);
        return false;
      })
      .then(function (written) {
        pending = null;
        return written;
      });

    return pending;
  }

  function syncWithin(ms) {
    return new Promise(function (resolve) {
      var timer = setTimeout(function () { resolve(false); }, ms);
      sync().then(function (written) {
        clearTimeout(timer);
        resolve(written);
      });
    });
  }

  window.imgMarketingParams = { read: readSessions, readSourceData: readAllSourceData, sync: sync, syncWithin: syncWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
